/**
 * Search customers by email, last name, or phone for mail order forms.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authenticatedFetch, getAuthHeaders } from '../utils/api';

const MailOrderCustomerSearch = ({ onSelect, disabled = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState('');
  const rootRef = useRef(null);
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (term) => {
    const trimmed = term.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearchError('');
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setSearchError('');
    try {
      const response = await authenticatedFetch(
        `/api/orders/mail-order/customers/search?q=${encodeURIComponent(trimmed)}&limit=8`,
        { headers: getAuthHeaders() }
      );
      let data = {};
      try {
        data = await response.json();
      } catch {
        throw new Error(response.ok ? 'Invalid search response' : 'Customer search failed');
      }
      if (!response.ok) throw new Error(data.error || 'Search failed');
      setResults(Array.isArray(data.customers) ? data.customers : []);
      setHasSearched(true);
    } catch (err) {
      setResults([]);
      setHasSearched(true);
      const message = err?.message || 'Customer search failed';
      setSearchError(
        /failed to fetch|networkerror|load failed/i.test(message)
          ? 'Cannot reach server — check that the backend is running.'
          : message
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!open || query.trim().length < 2) {
      setResults([]);
      setHasSearched(false);
      setSearchError('');
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setSearchError('');
    debounceRef.current = setTimeout(() => {
      runSearch(query);
    }, 280);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, runSearch]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const pickCustomer = (customer) => {
    onSelect?.(customer);
    setQuery('');
    setResults([]);
    setHasSearched(false);
    setSearchError('');
    setOpen(false);
  };

  return (
    <div className={`mail-order-customer-search${open ? ' is-open' : ''}`} ref={rootRef}>
      <input
        type="search"
        className="mail-order-customer-search__input"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search email, last name, or phone…"
        autoComplete="off"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
      />
      {loading && <span className="mail-order-customer-search__status">Searching…</span>}
      {searchError && !loading && (
        <span className="mail-order-customer-search__status mail-order-customer-search__status--error">
          {searchError}
        </span>
      )}
      {open && query.trim().length >= 2 && (
        <ul className="mail-order-customer-search__dropdown" role="listbox">
          {!loading && hasSearched && results.length === 0 && !searchError && (
            <li className="mail-order-customer-search__empty" role="option">
              No matches — try email, last name, or phone
            </li>
          )}
          {results.map((customer) => (
            <li key={`${customer.email}-${customer.userId || 'guest'}`} role="option">
              <button type="button" onClick={() => pickCustomer(customer)}>
                <span className="mail-order-customer-search__name">{customer.name || customer.email}</span>
                <span className="mail-order-customer-search__meta">
                  {customer.email}
                  {customer.phone ? ` · ${customer.phone}` : ''}
                  {customer.isWholesaler ? ' · wholesaler' : ''}
                  {customer.source === 'order' ? ' · past order' : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MailOrderCustomerSearch;
